import type { MarketTopic } from '../../types/market'

export const moneyTopics: MarketTopic[] = [
  {
    key: 'user-wallet',
    slug: 'wallet',
    title: 'Consulta tu wallet y sus movimientos',
    menu: 'Wallet y saldo',
    summary: 'Identifica el dinero disponible para comprar, los créditos reservados y el origen de cada movimiento.',
    kind: 'user',
    related: ['user-recharge', 'user-payment', 'user-withdraw', 'user-seller'],
    aliases: ['wallet', 'billetera', 'saldo', 'creditos', 'movimientos', 'saldo reservado', 'saldo disponible', 'reserva'],
    questions: ['por que no aparece saldo', 'que es saldo reservado', 'los creditos son wcoin', 'los creditos son zen', 'cuantos creditos es un usdt'],
    sections: [
      {
        id: 'saldo',
        title: 'Tu saldo disponible para el mercado',
        blocks: [
          { t: 'p', text: 'Abre Mi wallet desde el menú de tu cuenta. El número principal es el saldo disponible, expresado en la moneda web configurada por el administrador.' },
          { t: 'note', tone: 'info', text: 'Es el saldo web del Market. Su nombre visible es configurable (las capturas usan CREDITS) y no equivale a Zen, WCoin ni otra moneda o saldo del juego.' },
          { t: 'p', text: 'Los créditos disponibles provienen de recargas verificadas, ventas confirmadas o ajustes administrativos registrados. Se utilizan para comprar en cualquiera de los dos mercados o para solicitar retiros.' },
          { t: 'shot', id: '13' },
        ],
      },
      {
        id: 'reservado',
        title: 'Por qué puede aparecer saldo reservado',
        blocks: [
          { t: 'p', text: 'Cuando confirmas un retiro, los créditos correspondientes quedan reservados. Siguen asociados a la solicitud, pero no pueden gastarse en compras ni incluirse en otro retiro.' },
          { t: 'note', tone: 'info', text: 'Saldo disponible = saldo de la cuenta − reservas. Cuando el administrador registra el pago del retiro, se descuenta el importe; si lo rechaza, la reserva se libera y esos créditos vuelven a estar disponibles.' },
        ],
      },
      {
        id: 'movimientos',
        title: 'Lee el historial compacto de movimientos',
        blocks: [
          { t: 'p', text: 'La tabla muestra los últimos 200 movimientos, con identificador, tipo, fecha UTC, importe y saldo posterior. Los importes positivos indican entradas; los negativos, salidas.' },
          { t: 'table', head: ['Movimiento', 'Origen habitual'], rows: [['Recarga de wallet', 'Pago verificado y créditos autorizados.'], ['Venta de un ítem', 'Importe neto recibido de una publicación.'], ['Compra de ítems / Compra de carrito', 'Cobro de productos confirmados.'], ['Retiro a USDT', 'Salida registrada cuando se confirma el pago del retiro.'], ['Ajuste de saldo', 'Modificación realizada por la administración.']] },
          { t: 'p', text: 'Un retiro pendiente se refleja como reserva; todavía no constituye el movimiento definitivo de salida.' },
        ],
      },
      {
        id: 'detalle',
        title: 'Consulta el detalle en el módulo correspondiente',
        extended: true,
        blocks: [
          { t: 'p', text: 'La wallet es un resumen económico. Para conocer el ítem y la entrega usa Mis compras; para el TXID y estado del pago usa Ver mis recargas; para destino, comisión y transferencia de salida usa Retirar a Binance → Mis retiros.' },
          { t: 'p', text: 'Una recarga en estado Pago por procesar o Pago procesado no incrementa el saldo. Solo Pago verificado significa que los créditos fueron agregados.' },
        ],
      },
      {
        id: 'conversion',
        title: 'Plazos y conversión que utiliza el sistema',
        blocks: [
          { t: 'table', head: ['USDT', 'Créditos'], rows: [['5', '50'], ['10', '100'], ['12,50', '125']], caption: 'Conversión documentada: 1 USDT = 10 créditos' },
          { t: 'p', text: 'Después de pagar y reportar la transferencia, las recargas pueden demorar de 1 a 24 horas mientras se realiza la revisión manual. Al verificar el administrador, los créditos se registran en esa operación; no comienza otro plazo de espera. Los retiros tienen un plazo de revisión y envío de 1 a 24 horas.' },
          { t: 'p', text: 'La comisión de venta y la comisión de retiro son conceptos diferentes. Consulta el cálculo de cada operación en su pantalla; no se muestran como un único porcentaje de la wallet.' },
        ],
      },
    ],
  },
  {
    key: 'user-recharge',
    slug: 'recargar',
    title: 'Genera una recarga por Binance o billetera',
    menu: 'Recargar USDT',
    summary: 'Elige un destino habilitado, crea la orden y paga el importe exacto antes de reportar la transacción.',
    kind: 'user',
    related: ['user-payment', 'user-wallet', 'user-faq'],
    aliases: ['recarga', 'recargar', 'deposito', 'depositar', 'cargar saldo', 'comprar creditos', 'binance id', 'billetera', 'red', 'trc20', 'bep20', 'erc20', 'memo'],
    questions: ['como recargo', 'como recargo usdt', 'cuanto tarda la recarga', 'que red uso', 'generar la orden ya paga'],
    checklist: ['Elegí Binance ID o billetera y un destino habilitado', 'El importe está dentro de los límites y en múltiplos de 0,10', 'Generé la orden', 'Pagué con los datos de esa orden (destino, red y memo si corresponde)', 'Guardé el ID de transferencia o TXID', 'Lo reporté en la misma orden'],
    sections: [
      {
        id: 'metodos',
        title: 'Elige uno de los dos métodos',
        blocks: [
          { t: 'p', text: 'Abre Mi wallet → Recargar con USDT. Encontrarás dos opciones excluyentes:' },
          { t: 'list', items: ['Recargar por Binance ID: transferir USDT al ID de Binance configurado por la administración.', 'Recargar por billetera: enviar USDT a una dirección usando una de las redes habilitadas.'] },
          { t: 'p', text: 'Un método sin destinos habilitados aparece desactivado. La lista de redes y destinatarios depende de la configuración del mercado; no presupongas que una red siempre está disponible.' },
        ],
      },
      {
        id: 'importe',
        title: 'Selecciona el destino y el importe',
        blocks: [
          { t: 'steps', items: ['Elige el destinatario de Binance o la red de USDT.', 'Escribe el importe dentro del mínimo y máximo mostrados para ese destino.', 'Utiliza múltiplos de 0,10 USDT, que equivalen a créditos enteros.', 'Revisa los créditos que recibirás: 1 USDT = 10 créditos.', 'Pulsa Generar orden de recarga.'] },
          { t: 'shot', id: '14' },
          { t: 'shot', id: '15' },
          { t: 'p', text: 'Ejemplos: 5 USDT generan 50 créditos, 10 USDT generan 100 y 12,50 USDT generan 125, siempre que estén dentro del rango permitido.' },
        ],
      },
      {
        id: 'orden',
        title: 'Crear la orden todavía no realiza el pago',
        blocks: [
          { t: 'p', text: 'El Market guarda una orden ligada a tu cuenta con el método, destinatario, importe y créditos previstos. Después muestra los datos exactos que debes usar en Binance o en tu billetera.' },
          { t: 'note', tone: 'warn', text: 'Crear la orden no mueve fondos desde tu cuenta externa. Tampoco deposita créditos en la wallet del Market. El pago se realiza por tu cuenta desde la aplicación o servicio elegido.' },
        ],
      },
      {
        id: 'pagar',
        title: 'Paga utilizando los datos de esa orden',
        blocks: [
          { t: 'p', text: 'Binance ID: copia el ID de destino, comprueba el titular o alias y transfiere USDT. No confundas ese ID con una dirección blockchain.' },
          { t: 'p', text: 'Billetera: copia la dirección y usa exactamente la red indicada. Si la orden muestra Memo / etiqueta obligatoria, incluye también ese dato. El botón Copiar ayuda a trasladar la dirección y el memo.' },
          { t: 'p', text: 'Lee cualquier instrucción adicional del administrador. El importe de la orden debe llegar completo; las comisiones de tu proveedor se consideran por separado.' },
          { t: 'note', tone: 'warn', text: 'Las direcciones, Binance ID e importes de las capturas son ficticios. Usa siempre los datos de tu orden actual en el Market.' },
        ],
      },
      {
        id: 'referencia',
        title: 'Conserva la referencia para reportar el pago',
        blocks: [
          { t: 'p', text: 'Después de completar la transferencia, copia su identificador: ID de transferencia de Binance o TXID de la billetera. Regresa a la misma orden y completa el reporte de pago.' },
          { t: 'p', text: 'Una vez transferido, no crees un segundo pago solo porque el saldo todavía no aparece. El proceso requiere reporte y verificación manual; sigue el estado de la orden original.' },
        ],
      },
      {
        id: 'cuando',
        title: 'Cuándo aparecerán los créditos',
        blocks: [
          { t: 'p', text: 'La recarga puede demorar de 1 a 24 horas después de pagar y reportar. La administración primero vincula y procesa la transferencia y después la verifica para autorizar los créditos.' },
          { t: 'p', text: 'Esta versión opera manualmente con Binance ID personal y billeteras. No realiza una acreditación automática. El usuario puede reportar la transferencia sin depender de un aviso automático.' },
        ],
      },
    ],
  },
  {
    key: 'user-payment',
    slug: 'reportar-pago',
    title: 'Reporta la transferencia y sigue su verificación',
    menu: 'Reportar el pago',
    summary: 'Vincula el identificador de tu transferencia con la orden y distingue un pago procesado de uno acreditado.',
    kind: 'user',
    related: ['user-recharge', 'user-wallet', 'user-faq'],
    aliases: ['reportar pago', 'comprobante', 'txid', 'id de transferencia', 'pago procesado', 'pago verificado', 'requiere correccion', 'mis recargas'],
    questions: ['que significa procesado', 'pague y no tengo saldo', 'donde pongo el txid', 'puedo subir captura del pago', 'que hago si requiere correccion'],
    sections: [
      {
        id: 'enviar',
        title: 'Envía el comprobante dentro de la orden',
        blocks: [
          { t: 'steps', items: ['Abre Recargar con USDT → Mis recargas.', 'Pulsa Ver orden en la recarga que pagaste.', 'Pega el ID de transferencia de Binance o el TXID de la billetera.', 'Agrega un comentario opcional con información útil para la revisión.', 'Pulsa Enviar comprobante de pago.'] },
          { t: 'shot', id: '16' },
          { t: 'p', text: 'En esta versión, el comprobante es el identificador de la transferencia más el comentario. El formulario no contiene carga de imágenes, PDF ni otros archivos.' },
        ],
      },
      {
        id: 'identificadores',
        title: 'Distingue los identificadores que se usan',
        blocks: [
          { t: 'table', head: ['Dato', 'Qué identifica', 'Dónde usarlo'], rows: [['Orden del Market', 'Tu solicitud de recarga y el importe previsto.', 'Historial y consultas con el administrador.'], ['Binance ID de destino', 'La cuenta que recibe la transferencia.', 'Al pagar dentro de Binance.'], ['ID de transferencia / TXID', 'El pago que realizaste.', 'Campo para reportar el comprobante.'], ['Dirección de billetera', 'El destino en una red blockchain.', 'Al transferir por la red elegida.']] },
          { t: 'p', text: 'Pega el identificador completo del pago, sin enlaces ni espacios. El Binance ID del destinatario y tu dirección de billetera no sustituyen el identificador de la transacción.' },
        ],
      },
      {
        id: 'estados',
        title: 'Estados de una recarga',
        blocks: [
          { t: 'table', head: ['Estado visible', 'Qué ocurrió', '¿Aumenta el saldo?'], rows: [['Pago por procesar · Orden creada', 'Generaste la nota de pago; falta reportar la transferencia.', 'No.'], ['Pago por procesar · Pago reportado', 'Tu identificador y comentario quedaron ligados a la orden.', 'No.'], ['Pago procesado', 'La administración vinculó una transferencia y dejó el pago pendiente de verificación.', 'No.'], ['Pago verificado', 'El administrador autorizó la entrega de créditos.', 'Sí, por el importe de la orden.'], ['Requiere corrección', 'La revisión encontró datos que necesitan corregirse.', 'No.']] },
          { t: 'shot', id: '17' },
          { t: 'note', tone: 'warn', text: '«Procesado» no significa «acreditado». El identificador presentado por el usuario vincula el reporte con su cuenta y orden, pero no confirma por sí mismo que el dinero haya sido recibido.' },
        ],
      },
      {
        id: 'corregir',
        title: 'Corrige un reporte observado',
        blocks: [
          { t: 'p', text: 'Si aparece Requiere corrección, abre la orden y lee la respuesta del administrador. El formulario vuelve a estar disponible para corregir el identificador o ampliar el comentario.' },
          { t: 'p', text: 'Si ya transferiste, corrige el reporte sin volver a pagar. Cuando el estado es Pago procesado o Pago verificado, la referencia confirmada por la administración se muestra por separado de la que reportaste.' },
        ],
      },
      {
        id: 'historial',
        title: 'Consulta el historial de recargas',
        extended: true,
        blocks: [
          { t: 'p', text: 'Mis recargas muestra orden, fecha UTC, método, destino, importe USDT, créditos y estado. Usa Ver orden para consultar las referencias reportada y vinculada, comentarios y fechas de procesamiento o revisión.' },
          { t: 'p', text: 'El historial ofrece navegación por páginas de 50 órdenes. Una recarga anterior sigue consultable aunque se deshabilite el destino para nuevas operaciones.' },
        ],
      },
      {
        id: 'plazo',
        title: 'Si transcurre el plazo de revisión',
        blocks: [
          { t: 'p', text: 'Si después del plazo de 1 a 24 horas no se ha resuelto tu reporte, contacta a la administración con el número de orden, método, importe, identificador de transferencia y estado visible. No compartas tu contraseña.' },
          { t: 'p', text: 'Si la orden está verificada y no reconoces el saldo, consulta los movimientos posteriores: los créditos podrían haberse usado en compras o estar reservados para un retiro.' },
        ],
      },
    ],
  },
  {
    key: 'user-withdraw',
    slug: 'retirar-usdt',
    title: 'Solicita un retiro de créditos a USDT',
    menu: 'Retirar USDT',
    summary: 'Registra el destino, revisa la comisión y confirma una solicitud con créditos reservados y envío manual.',
    kind: 'user',
    related: ['user-wallet', 'user-seller', 'user-faq'],
    aliases: ['retiro', 'retirar', 'retirar usdt', 'sacar saldo', 'cobrar', 'binance id', 'comision de retiro', 'mis retiros', '72 horas'],
    questions: ['como retiro usdt', 'donde pongo binance id', 'cuanto queda de comision', 'por que no puedo retirar', 'mi retiro fue rechazado', 'puedo cancelar un retiro'],
    checklist: ['Tengo al menos 100 créditos disponibles', 'No tengo otro retiro activo y pasaron 72 horas desde la última solicitud', 'Guardé mi Binance ID (no una dirección blockchain)', 'Revisé destino, créditos reservados, bruto, comisión y neto', 'Confirmé la solicitud y la veo en Mis retiros'],
    sections: [
      {
        id: 'condiciones',
        title: 'Condiciones para solicitar el retiro',
        blocks: [
          { t: 'table', head: ['Condición', 'Regla documentada'], rows: [['Destino', 'Binance ID registrado en tu perfil de retiros.'], ['Conversión', '10 créditos = 1 USDT.'], ['Mínimo', '10 USDT antes de comisión, equivalentes a 100 créditos.'], ['Frecuencia', 'Una solicitud cada 72 horas, contadas desde la última solicitud.'], ['Plazo', 'Revisión y envío manual de 1 a 24 horas.'], ['Saldo', 'Créditos disponibles suficientes y ningún retiro activo.']] },
          { t: 'p', text: 'El porcentaje de comisión es configurable. Revisa el porcentaje actual y el neto en la pantalla; no asumas que siempre será 5 %.' },
        ],
      },
      {
        id: 'binance-id',
        title: 'Registra tu Binance ID de destino',
        blocks: [
          { t: 'steps', items: ['Abre Mi wallet → Retirar a Binance.', 'En Tu Binance ID, escribe el identificador numérico de tu cuenta de Binance.', 'Agrega opcionalmente un titular o alias.', 'Pulsa Guardar Binance ID.'] },
          { t: 'shot', id: '18' },
          { t: 'note', tone: 'warn', text: 'No escribas una dirección blockchain ni el ID de una transferencia. Puedes usar Modificar destino para actualizar el perfil. Los retiros que ya creaste conservan el destino confirmado; editar el perfil no cambia esas solicitudes.' },
        ],
      },
      {
        id: 'importe',
        title: 'Solicita el importe bruto y revisa el neto',
        blocks: [
          { t: 'p', text: 'Escribe el importe en USDT antes de comisión, en múltiplos de 0,10 USDT, y pulsa Revisar retiro. Verás el Binance ID, créditos que se reservarán, importe solicitado, porcentaje, comisión y neto que recibirás.' },
          { t: 'table', example: true, caption: 'Ejemplo con una comisión del 5 % (no es la tarifa vigente)', head: ['Ejemplo', 'Créditos', 'Bruto', 'Comisión', 'Neto'], rows: [['Retiro mínimo', '100', '10,00 USDT', '0,50 USDT', '9,50 USDT'], ['Retiro mayor', '250', '25,00 USDT', '1,25 USDT', '23,75 USDT'], ['Redondeo', '101', '10,10 USDT', '0,51 USDT', '9,59 USDT']] },
          { t: 'note', tone: 'example', text: 'La comisión se redondea hacia arriba al siguiente centavo de USDT. El mínimo de 10 USDT es bruto: con una comisión del 5 %, el retiro mínimo entrega 9,50 USDT, no 10 USDT netos.' },
        ],
      },
      {
        id: 'confirmar',
        title: 'Confirma la solicitud y la reserva',
        blocks: [
          { t: 'p', text: 'Marca la casilla que confirma el destino y el importe neto. Pulsa Confirmar solicitud de retiro. Los créditos se reservan inmediatamente y dejan de estar disponibles para compras.' },
          { t: 'shot', id: '19' },
          { t: 'p', text: 'Esto crea una solicitud; no hace una transferencia automática desde Binance. La administración revisa y realiza el envío manual. Si cambió el porcentaje o el destino mientras preparabas la solicitud, el Market te pedirá revisarla otra vez.' },
        ],
      },
      {
        id: 'estados',
        title: 'Entiende el estado de tu retiro',
        blocks: [
          { t: 'table', head: ['Estado', 'Qué significa para ti'], rows: [['Pendiente', 'La solicitud se recibió. Los créditos están reservados.'], ['En proceso', 'La administración está tramitando el envío. La reserva continúa.'], ['Pagado', 'El administrador registró el envío; puedes consultar el ID de la transferencia. El cargo de créditos queda confirmado.'], ['Rechazado', 'La reserva se libera. Lee la respuesta del administrador.']] },
          { t: 'p', text: 'El plazo de 72 horas se conserva aunque una solicitud sea rechazada. El panel muestra la próxima fecha disponible en UTC cuando corresponde.' },
        ],
      },
      {
        id: 'mis-retiros',
        title: 'Consulta Mis retiros y los datos confirmados',
        extended: true,
        blocks: [
          { t: 'p', text: 'El historial Mis retiros muestra referencia, fecha, créditos, neto, comisión y estado. En Ver retiro aparecen el destino confirmado, importes, respuesta administrativa y, si está pagado, la referencia de la transferencia.' },
          { t: 'note', tone: 'info', text: 'Solo puedes tener un retiro activo. La interfaz no ofrece cancelación directa de solicitudes ni modificación de un retiro ya creado; si detectas un destino incorrecto, avisa a la administración antes del envío con la referencia del retiro.' },
        ],
      },
    ],
  },
]
